import { useTranslation } from 'react-i18next';
import { formatMoney, type CreditCustomer } from '@/lib/credit';

interface Props {
  customer: CreditCustomer;
  currency: string;
  /** Solde signé : > 0 = le client doit de l'argent (dette). */
  balance: number;
  onChoose: (mode: 'payment' | 'deposit') => void;
  onClose: () => void;
}

/**
 * PayChoiceModal — après avoir appuyé sur « Il paie », le marchand choisit,
 * en deux grands boutons, s'il s'agit d'un paiement de dette ou d'un dépôt
 * de fonds. Dans les deux cas le calcul est automatique : la dette est
 * déduite en premier, le surplus part dans le solde magasin du client.
 */
export default function PayChoiceModal({ customer, currency, balance, onChoose, onClose }: Props) {
  const { t } = useTranslation();
  const debt = Math.max(0, balance);
  const store = Math.max(0, -balance);

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50" onClick={onClose} />
      <div className="fixed z-50 inset-x-0 bottom-0 sm:inset-0 sm:flex sm:items-center sm:justify-center sm:p-4">
        <div className="w-full sm:max-w-md bg-background-50 rounded-t-2xl sm:rounded-2xl border border-background-200/70 overflow-hidden">
          <div className="flex items-center justify-between px-5 h-14 border-b border-background-200/70">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-9 h-9 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
                <i className="ri-hand-coin-line text-lg"></i>
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-foreground-950 truncate">{t('crf.pay_title', { defaultValue: 'Il paie' })}</h3>
                <p className="text-xs text-foreground-500 truncate">{customer.full_name}</p>
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer flex-shrink-0">
              <i className="ri-close-line text-foreground-500"></i>
            </button>
          </div>

          <div className="p-5 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl bg-red-50 border border-red-200/60 px-3 py-2.5">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-red-600">{t('crf.owes', { defaultValue: 'Doit' })}</p>
                <p className="text-base font-bold text-red-700">{formatMoney(debt, currency)}</p>
              </div>
              <div className="rounded-xl bg-emerald-50 border border-emerald-200/60 px-3 py-2.5">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700">{t('crf.holds', { defaultValue: 'A chez vous' })}</p>
                <p className="text-base font-bold text-emerald-700">{formatMoney(store, currency)}</p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => onChoose('payment')}
              className="w-full flex items-center gap-3 p-4 rounded-2xl border border-accent-200 bg-accent-50 hover:bg-accent-100 transition-colors cursor-pointer text-left"
            >
              <span className="w-12 h-12 rounded-full bg-accent-500 text-background-50 flex items-center justify-center flex-shrink-0">
                <i className="ri-arrow-up-line text-2xl"></i>
              </span>
              <span className="min-w-0">
                <span className="block text-base font-bold text-foreground-950">{t('crf.pay_debt', { defaultValue: 'Paiement de dette' })}</span>
                <span className="block text-xs text-foreground-500">{t('crf.pay_debt_hint', { defaultValue: 'La dette est déduite, le reste va dans son solde.' })}</span>
              </span>
            </button>

            <button
              type="button"
              onClick={() => onChoose('deposit')}
              className="w-full flex items-center gap-3 p-4 rounded-2xl border border-secondary-200 bg-secondary-50 hover:bg-secondary-100 transition-colors cursor-pointer text-left"
            >
              <span className="w-12 h-12 rounded-full bg-secondary-500 text-background-50 flex items-center justify-center flex-shrink-0">
                <i className="ri-wallet-3-line text-2xl"></i>
              </span>
              <span className="min-w-0">
                <span className="block text-base font-bold text-foreground-950">{t('crf.deposit_funds', { defaultValue: 'Dépôt de fonds' })}</span>
                <span className="block text-xs text-foreground-500">{t('crf.deposit_hint', { defaultValue: 'Argent déposé en avance, utilisable chez vous.' })}</span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
}